import { FC } from 'react';
import { t } from 'i18next';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Form, FormProvider } from 'react-hook-form';

import {
  NumberInputFormField,
  ProviderSearchFormField,
  SelectFormField,
  TextAreaFormField,
} from 'components/DynamicFormFields';
import {
  ACTIVITY_MINUTES_KEYS,
  MAX_ACTIVITY_MINUTES,
  useAddCareNote,
} from '@views/pages/Patient/components/CareNotes/hooks';
import {
  CustomModal,
  NumberStepper,
  SaveChangesModal,
  StatusBanner,
} from '@components';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants';

interface AddCareNoteProps {
  patientId: string;
  variant: 'edit' | 'create';
  disabled: boolean;
  note_id?: string;
}

export const AddCareNote: FC<AddCareNoteProps> = ({
  patientId,
  variant,
  note_id = '',
  disabled = false,
}) => {
  const {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    notesTypeOptions,
    programOptions,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    isCCMLimitWarningDisplayed,
    careManagementActivitiesEditable,
    activityLabels,
    handleActivityChange,
  } = useAddCareNote(patientId, note_id, variant);

  const { control, watch, setValue } = formMethods;
  const notableCall = watch('notable_call');
  const activityMinutes = watch('activity_minutes');

  return (
    <>
      {variant === 'create' ? (
        <Button
          sx={{ minHeight: '30px' }}
          onClick={handleOpen}
          startIcon={
            <Add
              sx={{ height: '20px' }}
              color={disabled ? 'disabled' : 'primary'}
            />
          }
          disabled={disabled}
          variant='outlined'
        >
          {t('patient.careNotes.add')}
        </Button>
      ) : (
        <IconButton
          sx={{
            '& .MuiIconButton': {
              padding: '0.5rem',
            },
          }}
          disabled={disabled}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            handleOpen();
          }}
        >
          <Edit color={disabled ? 'disabled' : 'primary'} />
        </IconButton>
      )}
      <Box onClick={e => e.stopPropagation()}>
        <CustomModal
          isOpen={isOpen && !disabled}
          onClose={handleClose}
          isLoading={isSubmitting}
          title={t(`patient.careNotes.modal.title.${variant}`)}
        >
          <FormProvider {...formMethods}>
            <Form
              control={control}
              onSubmit={e => onSubmit(e.event)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {isCCMLimitWarningDisplayed && (
                <StatusBanner
                  text={t(`patient.careNotes.ccmWarning`)}
                  variant='lightWarning'
                />
              )}

              <SelectFormField
                name='program_id'
                label={t('patient.careNotes.modal.labels.program')}
                options={programOptions}
                optional
              />

              <SelectFormField
                name='note_type'
                label={t('patient.careNotes.modal.labels.note_type')}
                options={notesTypeOptions}
              />

              <ProviderSearchFormField
                name='provider_id'
                placeholder={t('patient.careNotes.modal.labels.providerId')}
                optional
              />

              <TextAreaFormField
                required
                maxRows={4}
                name='note'
                disabled={disabled}
                label={t('patient.careNotes.modal.labels.note')}
                charLimit={MAXIMUM_TEXT_AREA_LENGTH}
              />

              {careManagementActivitiesEditable && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant='subtitle2'>
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.title',
                    )}
                  </Typography>
                  {ACTIVITY_MINUTES_KEYS.map(key => {
                    const othersSum = ACTIVITY_MINUTES_KEYS.reduce(
                      (acc, k) =>
                        k === key
                          ? acc
                          : acc + Number(activityMinutes?.[k] ?? 0),
                      0,
                    );

                    return (
                      <NumberStepper
                        key={key}
                        label={activityLabels[key]}
                        value={Number(activityMinutes?.[key] ?? 0)}
                        max={MAX_ACTIVITY_MINUTES - othersSum}
                        disabled={!careManagementActivitiesEditable}
                        onChange={value => handleActivityChange(key, value)}
                      />
                    );
                  })}
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.resetNote',
                    )}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 'medium', textAlign: 'right' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.remainingMinutes',
                      {
                        minutes:
                          MAX_ACTIVITY_MINUTES -
                          ACTIVITY_MINUTES_KEYS.reduce(
                            (acc, key) =>
                              acc + Number(activityMinutes?.[key] ?? 0),
                            0,
                          ),
                      },
                    )}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.monthlyResetNote',
                    )}
                  </Typography>
                </Box>
              )}

              <NumberInputFormField
                name='recorded_minutes'
                label={t(
                  `patient.careNotes.modal.labels.${careManagementActivitiesEditable ? 'total_minutes' : 'minutes'}`,
                )}
                optional={careManagementActivitiesEditable}
                disabled={careManagementActivitiesEditable}
              />

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={(event, checked) =>
                        setValue('notable_call', checked)
                      }
                      checked={notableCall}
                    />
                  }
                  label={t('patient.careNotes.modal.labels.notable_call')}
                />
              </FormGroup>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  pb: 3,
                }}
              >
                <Button
                  variant='text'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                  }}
                  disabled={isSubmitting}
                  onClick={handleClose}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'white',
                  }}
                  disabled={isSubmitting || isEditSubmitting || !isValid}
                  loading={isEditSubmitting}
                  loadingPosition='start'
                >
                  {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
                </Button>
              </Box>
              <SaveChangesModal
                isOpen={isSaveChangesModalOpen}
                onClose={handleCloseSaveChangesModal}
                isValid={isValid}
                handleSaveChanges={handleSaveChanges}
                discardChanges={handleDiscardChanges}
                isLoading={isEditSubmitting}
              />
            </Form>
          </FormProvider>
        </CustomModal>
      </Box>
    </>
  );
};
